// Справка „Прехвърляния": ВСИЧКИ складове по подразбиране (прехвърлянето е
// между два склада — изборът на склад в модула не го ограничава), KPI само по
// активните, сегменти Всички / Активни / Анулирани с фасетни броячи (пряк път
// към филтъра „Статус"), търсачка, филтри В ЗАГЛАВИЯТА НА КОЛОНИТЕ (От склад ·
// Към склад · Потребител · Статус) с ред „Филтри: …" над таблицата,
// сортиране по заглавията, „Покажи още" и документът при клик. „Печат" и
// менюто „⋯" са на реда (Принтирай · Преглед · Редактирай · Анулирай). Горе —
// общата лента (период · Excel · Опресни; Excel-ът — `transfersExcel`).
// Периодът по подразбиране е „Последните 7 дни" — както в стария списък.
import { useMemo, useState, type FC } from "react";
import { AppstoreOutlined, EuroOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useTransfersReport } from "../../../../hooks/useInventoryTransfers";
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import { exportToExcel } from "../../../../utils/excelExport";
import { InventoryTheme } from "../../components/InventoryTheme";
import { PageHeader } from "../../components/list/PageHeader";
import {
  EmptyScreen,
  KpiGrid,
  SearchBox,
  SectionCard,
  SegmentedCounts,
  type KpiCard,
} from "../../components/list/ListBits";
import type { RowAction } from "../../components/list/RowActionsMenu";
import { ReportToolbar } from "../../components/list/ReportToolbar";
import { refreshOf } from "../../components/list/reportRefresh";
import { useShowMore } from "../../components/list/useShowMore";
import { periodOfPreset, periodToParams, type Period } from "../../components/period/periodPresets";
import { FilterSummaryLine } from "../../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../../components/filters/useColumnFilters";
import { formatMoney } from "../../components/numbers";
import { useTransferDocument } from "../../transfer/components/useTransferDocument";
import { isTransferAnnulled } from "../../transfer/components/transferMeta";
import { ReportLoading, ReportNoMatch, ShowMoreRow } from "../components/ReportListBits";
import {
  EMPTY_TRANSFER_FILTERS,
  TEXT_SORT_KEYS,
  searchTransfers,
  sortTransfers,
  statusSegmentOf,
  transferFilterSpecs,
  transferTotals,
  type TransferFilterKey,
  type TransferSort,
  type TransferSortKey,
  type TransferStatusKey,
} from "./transfersReportModel";
import { TransfersReportTable, type TransfersDensity } from "./TransfersReportTable";
import { transferExcelColumns, transferExcelRows } from "./transfersExcel";

// Проповете от layout-а (Inventory.tsx) не ѝ трябват: справката е по ВСИЧКИ
// складове, а складът на модула не я ограничава; типът само ги приема.
type LayoutProps = { warehouseId?: number | null };

export const TransfersReport: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const { isNarrow, isWide } = useViewport();
  const transferDocument = useTransferDocument();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("last7"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<TransferSort>({ key: "date", dir: "desc" });
  const transfersQuery = useTransfersReport({
    ...periodToParams(period),
    status: "all",
  });
  const { data: transfers = [], isLoading } = transfersQuery;
  const { refresh, refreshing } = refreshOf(transfersQuery);

  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const specs = useMemo(
    () =>
      transferFilterSpecs({
        titles: {
          from: t("inventoryPage.colFromWarehouse"),
          to: t("inventoryPage.colToWarehouse"),
          users: t("inventoryPage.colUser"),
          statuses: t("inventoryPage.colStatus"),
        },
        statusLabel: (value) =>
          value === "annulled" ? t("inventoryPage.segAnnulled") : t("inventoryPage.trxSegActive"),
      }),
    [t],
  );
  const searched = useMemo(() => searchTransfers(transfers, query), [transfers, query]);
  const filters = useColumnFilters({ specs, empty: EMPTY_TRANSFER_FILTERS, universe: transfers, base: searched });
  const shown = useMemo(() => sortTransfers(filters.rows, sort), [filters.rows, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, [period, filters.state, query, sort]);
  const periodTotals = transferTotals(transfers);
  const shownTotals = transferTotals(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };

  const sections: ColumnSections<TransferFilterKey> = {
    from: filters.section("from"),
    to: filters.section("to"),
    users: filters.section("users"),
    statuses: filters.section("statuses"),
  };

  // Сегментите = филтърът „Статус" (броячите — фасетни, както в попъпа)
  const statusCounts = new Map(filters.facet("statuses").map((option) => [option.value, option.count]));
  const onStatusSegment = (key: TransferStatusKey) =>
    filters.setValue("statuses", key === "all" ? [] : [key]);

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.kpiTransfers"), value: String(periodTotals.count), Icon: SwapOutlined },
    { label: t("inventoryPage.colProducts"), value: String(periodTotals.products), Icon: AppstoreOutlined },
    {
      label: t("inventoryPage.kpiValueGross"),
      value: formatMoney(periodTotals.gross),
      Icon: EuroOutlined,
      tone: "brand",
    },
  ];

  const onSort = (key: TransferSortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: TEXT_SORT_KEYS.includes(key) ? "asc" : "desc" },
    );

  // Менюто „⋯" на реда; анулираният документ се само преглежда и печата
  const actionsOf = (transfer: TransferDocument): RowAction[] => {
    const base: RowAction[] = [
      {
        key: "print",
        label: t("inventoryPage.print"),
        onClick: () => transferDocument.open(transfer, { autoPrint: true }),
      },
      {
        key: "view",
        label: t("inventoryPage.viewDocument"),
        onClick: () => transferDocument.open(transfer),
      },
    ];
    if (isTransferAnnulled(transfer)) return base;
    return [
      ...base,
      {
        key: "edit",
        label: t("inventoryPage.docEdit"),
        dividerBefore: true,
        onClick: () => transferDocument.edit(transfer),
      },
      {
        key: "annul",
        label: t("inventoryPage.docDelete"),
        danger: true,
        onClick: () => transferDocument.annul(transfer),
      },
    ];
  };

  // Каквото е на екрана (филтри, търсене, подредба) + редът „Общо"
  const exportXls = () =>
    exportToExcel({
      filename: "prehvarlyaniya",
      sheetName: t("inventoryPage.navTransfersReport"),
      rows: transferExcelRows(shown, {
        annulled: t("inventoryPage.statusAnnulled"),
        total: t("inventoryPage.periodTotalActive"),
      }),
      columns: transferExcelColumns(t),
    });

  const density: TransfersDensity = isNarrow ? "cards" : isWide ? "full" : "medium";

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.navTransfersReport")}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedCounts
              value={statusSegmentOf(filters.state.statuses)}
              onChange={onStatusSegment}
              options={[
                { key: "all", label: t("inventoryPage.segAll"), count: filters.facetTotal("statuses") },
                { key: "active", label: t("inventoryPage.trxSegActive"), count: statusCounts.get("active") ?? 0 },
                { key: "annulled", label: t("inventoryPage.segAnnulled"), count: statusCounts.get("annulled") ?? 0 },
              ]}
            />
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.transfersSearchPh")} />
          </div>
          <SectionCard>
            <FilterSummaryLine
              countLabel={t("inventoryPage.transfersResult", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <span>
                {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(shownTotals.gross)}</span>
              </span>
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : transfers.length === 0 ? (
              <EmptyScreen Icon={SwapOutlined} tone="brand" title={t("inventoryPage.transfersNone")} />
            ) : (
              <TransfersReportTable
                rows={visible}
                density={density}
                sort={sort}
                onSort={onSort}
                filters={sections}
                totalNet={shownTotals.net}
                totalGross={shownTotals.gross}
                onOpen={(transfer) => transferDocument.open(transfer)}
                onPrint={(transfer) => transferDocument.open(transfer, { autoPrint: true })}
                actionsOf={actionsOf}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>
        {transferDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default TransfersReport;
